// Admin media library — drag/drop or click to upload, grid of thumbnails,
// copy-URL for pasting into room descriptions, categorize, soft-delete.
import { useEffect, useRef, useState } from "react";
import { UploadCloud, Copy, Trash2, X, ImageIcon, Film, FileText, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { adminApi, formatApiError } from "@/lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = [
    { key: "all", label: "All" },
    { key: "room", label: "Rooms" },
    { key: "gallery", label: "Gallery" },
    { key: "wedding", label: "Weddings" },
    { key: "other", label: "Other" },
];

function iconFor(ct) {
    if (!ct) return ImageIcon;
    if (ct.startsWith("image/")) return ImageIcon;
    if (ct.startsWith("video/")) return Film;
    return FileText;
}

function bytesLabel(n) {
    if (n == null) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export default function AdminMedia() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState("all");
    const [q, setQ] = useState("");
    const [uploads, setUploads] = useState([]); // in-progress
    const [uploadCat, setUploadCat] = useState("room");
    const [preview, setPreview] = useState(null);
    const inputRef = useRef(null);
    const dropRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (category !== "all") params.set("category", category);
            if (q.trim()) params.set("q", q.trim());
            const { data } = await adminApi.get(`/admin/media?${params.toString()}`);
            setItems(data);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    };

    useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [category, q]);

    const upload = async (files) => {
        for (const file of Array.from(files)) {
            const uploadId = crypto.randomUUID();
            setUploads((u) => [...u, { id: uploadId, name: file.name, progress: 0 }]);
            try {
                const fd = new FormData();
                fd.append("file", file);
                fd.append("category", uploadCat);
                await adminApi.post("/admin/media/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                    onUploadProgress: (evt) => {
                        const pct = evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0;
                        setUploads((u) => u.map((x) => (x.id === uploadId ? { ...x, progress: pct } : x)));
                    },
                });
                toast.success(`Uploaded ${file.name}`);
            } catch (err) {
                toast.error(`${file.name}: ${formatApiError(err)}`);
            } finally {
                setUploads((u) => u.filter((x) => x.id !== uploadId));
            }
        }
        await load();
    };

    const onDrop = (e) => {
        e.preventDefault();
        dropRef.current?.classList.remove("border-gold");
        if (e.dataTransfer?.files?.length) upload(e.dataTransfer.files);
    };

    const copyUrl = (path) => {
        const abs = `${API.replace(/\/api$/, "")}/api/media/${path}`;
        navigator.clipboard?.writeText(abs);
        toast.success("URL copied");
    };

    const remove = async (id, name) => {
        if (!window.confirm(`Remove "${name}"? It will be hidden from the site (soft delete).`)) return;
        try {
            await adminApi.delete(`/admin/media/${id}`);
            setItems((x) => x.filter((i) => i.id !== id));
            toast.success("Removed");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div>
            <header className="mb-6">
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Content</div>
                <h1 className="font-display text-4xl md:text-5xl text-[#0b1f22] mt-3">Media library</h1>
                <p className="text-[#0b1f22]/60 mt-2 text-sm">Upload photos, videos, and PDFs. Each file gets a public URL you can paste into room descriptions, the gallery, or brochures.</p>
            </header>

            {/* Drop zone */}
            <div
                ref={dropRef}
                onDragOver={(e) => { e.preventDefault(); dropRef.current?.classList.add("border-gold"); }}
                onDragLeave={() => dropRef.current?.classList.remove("border-gold")}
                onDrop={onDrop}
                onClick={() => inputRef.current?.click()}
                data-testid="upload-dropzone"
                className="cursor-pointer border-2 border-dashed border-[#0b1f22]/20 rounded-2xl p-8 text-center bg-white hover:border-gold transition-colors mb-6"
            >
                <UploadCloud size={30} className="mx-auto text-[#0b1f22]/50" />
                <div className="mt-3 font-medium">Drag files here or click to browse</div>
                <div className="text-[11px] text-[#0b1f22]/55 mt-1">JPG · PNG · WebP · GIF · MP4 · MOV · PDF · up to 100 MB</div>
                <div className="mt-4 flex items-center justify-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#0b1f22]/55">Save into:</span>
                    <select value={uploadCat} onChange={(e) => setUploadCat(e.target.value)} onClick={(e) => e.stopPropagation()}
                        className="border border-[#0b1f22]/15 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.14em] bg-white">
                        <option value="room">Rooms</option><option value="gallery">Gallery</option>
                        <option value="wedding">Weddings</option><option value="other">Other</option>
                    </select>
                </div>
                <input ref={inputRef} type="file" multiple hidden onChange={(e) => e.target.files?.length && upload(e.target.files)}
                    accept="image/*,video/*,application/pdf" data-testid="upload-input" />
            </div>

            {/* In-progress */}
            {uploads.length > 0 && (
                <div className="bg-white border border-[#0b1f22]/10 rounded-2xl p-4 mb-6 space-y-3" data-testid="upload-progress">
                    {uploads.map((u) => (
                        <div key={u.id}>
                            <div className="flex items-center justify-between text-xs">
                                <span className="inline-flex items-center gap-2 text-[#0b1f22]/75"><Loader2 size={12} className="animate-spin" /> {u.name}</span>
                                <span className="text-[#0b1f22]/55">{u.progress}%</span>
                            </div>
                            <div className="mt-2 h-1.5 bg-[#0b1f22]/10 rounded-full overflow-hidden">
                                <div className="h-full bg-gold transition-all" style={{ width: `${u.progress}%` }} />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Filter bar */}
            <div className="flex flex-wrap gap-2 mb-4 items-center">
                {CATEGORIES.map((c) => (
                    <button key={c.key} onClick={() => setCategory(c.key)}
                        className={`text-[11px] uppercase tracking-[0.14em] px-3 py-1.5 rounded-full transition-colors ${category === c.key ? "bg-[#0b1f22] text-ivory" : "bg-[#0b1f22]/[0.06] text-[#0b1f22]/70 hover:bg-[#0b1f22]/10"}`}>
                        {c.label}
                    </button>
                ))}
                <div className="ml-auto flex items-center gap-2 border border-[#0b1f22]/15 rounded-full px-3 py-1.5 min-w-[220px]">
                    <Search size={13} className="text-[#0b1f22]/40" />
                    <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search filename or alt text…" className="flex-1 bg-transparent outline-none text-sm" />
                </div>
            </div>

            {/* Grid */}
            {loading ? (
                <div className="text-sm text-[#0b1f22]/50">Loading media…</div>
            ) : items.length === 0 ? (
                <div className="bg-white border border-[#0b1f22]/10 rounded-2xl p-10 text-center">
                    <ImageIcon size={30} className="mx-auto text-[#0b1f22]/30" />
                    <p className="text-sm text-[#0b1f22]/60 mt-3">No media in this category yet. Drop a file above to add one.</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {items.map((m) => {
                        const isImg = m.content_type?.startsWith("image/");
                        const url = `${API.replace(/\/api$/, "")}/api/media/${m.storage_path}`;
                        const Icon = iconFor(m.content_type);
                        return (
                            <figure key={m.id} className="group bg-white border border-[#0b1f22]/10 rounded-2xl overflow-hidden">
                                <button className="w-full aspect-square bg-[#0b1f22]/5 flex items-center justify-center overflow-hidden" onClick={() => setPreview(m)}>
                                    {isImg ? (
                                        <img src={url} alt={m.alt_text || m.display_name} loading="lazy" className="w-full h-full object-cover" />
                                    ) : (
                                        <Icon size={40} className="text-[#0b1f22]/40" />
                                    )}
                                </button>
                                <figcaption className="p-3 text-xs">
                                    <div className="font-medium truncate">{m.display_name || m.original_filename}</div>
                                    <div className="text-[#0b1f22]/55 mt-0.5">{bytesLabel(m.size)} · {m.category}</div>
                                    <div className="mt-2 flex gap-2">
                                        <button onClick={() => copyUrl(m.storage_path)} className="flex-1 border border-[#0b1f22]/15 rounded-full px-2 py-1.5 text-[10px] uppercase tracking-[0.14em] hover:border-gold hover:text-gold inline-flex items-center justify-center gap-1"><Copy size={11} /> Copy URL</button>
                                        <button onClick={() => remove(m.id, m.display_name || m.original_filename)} className="border border-red-200 text-red-500 hover:bg-red-50 rounded-full p-1.5"><Trash2 size={12} /></button>
                                    </div>
                                </figcaption>
                            </figure>
                        );
                    })}
                </div>
            )}

            {/* Preview modal */}
            {preview && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-[#0b1f22]/85 backdrop-blur-sm" onClick={() => setPreview(null)}>
                    <button className="absolute top-6 right-6 text-white/70 hover:text-white" onClick={() => setPreview(null)}><X size={24} /></button>
                    <div className="max-w-5xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
                        {preview.content_type?.startsWith("image/") ? (
                            <img src={`${API.replace(/\/api$/, "")}/api/media/${preview.storage_path}`} alt="" className="w-full h-full object-contain max-h-[75vh]" />
                        ) : preview.content_type?.startsWith("video/") ? (
                            <video src={`${API.replace(/\/api$/, "")}/api/media/${preview.storage_path}`} controls autoPlay className="w-full max-h-[75vh]" />
                        ) : (
                            <div className="bg-white p-8 rounded-2xl text-center">
                                <FileText size={48} className="mx-auto text-[#0b1f22]/50" />
                                <p className="mt-3 text-sm">{preview.display_name}</p>
                                <a href={`${API.replace(/\/api$/, "")}/api/media/${preview.storage_path}`} target="_blank" rel="noreferrer" className="mt-4 inline-block bg-gold text-navy rounded-full px-5 py-2 text-[11px] uppercase tracking-[0.22em] font-bold">Open</a>
                            </div>
                        )}
                        <div className="mt-3 text-center text-xs text-white/70">
                            {preview.display_name || preview.original_filename} · {bytesLabel(preview.size)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
